import { useAppDispatch, useAppSelector } from '../app/hooks';
import { useConfiguratorState } from '../app/configurator-logic';
import styles from './debug-info.module.css';
import { ReactNode, useState } from 'react';
import { configuratorActions, contentSelector, implicitValuesByGroupSelector, justChangedSelector, modeSelector, selectedElementSelector, selectedGroupSelector } from '../app/configurator-slice';
import { useGetOptionsQuery } from '../app/api-slice';
import { useParams } from 'react-router-dom';
import { resolveAttachControlsInfo } from '../app/composition-helpers';
import { useDataRequestLang } from '../app/main-slice';
import { viewerContentSelector } from '../app/viewer-slice';
import { catalogSelector, questionPositionSelector } from '../app/configurator-selectors';
import { MAIN_SCENE } from '../app/types/composition';

export const NOT_READY_YET = ({ children}: { children: ReactNode }) => {
    return null

}

export const DebugValues = ({ className }: {
    className?: string
}) => {

    const lang = useDataRequestLang();
    const { familyId, variantId } = useParams();
    const { canGoPrevious, canGoNext } = useConfiguratorState();

    const mode = useAppSelector(modeSelector);
    const justChanged = useAppSelector(justChangedSelector);
    const implicitValues = useAppSelector(implicitValuesByGroupSelector);
    const [ currentQuestion, currentNSteps, currentStep, questionData ] = useAppSelector(questionPositionSelector);
    const composition = useAppSelector(contentSelector);
    const viewerContent = useAppSelector(viewerContentSelector);
    const selectedElement = useAppSelector(selectedElementSelector);
    const selectedGroup = useAppSelector(selectedGroupSelector);
    const { data: options, isSuccess: optionsSuccess } = useGetOptionsQuery({
        lang,
        familyId: familyId!, 
        variantId
    });
    const { elements } = useAppSelector(catalogSelector);

    const dispatch = useAppDispatch();

    const [ showProperties, setShowProperties ] = useState(false);
    const [ showWorkState, setShowWorkState ] = useState(false);
    const [ showComposition, setShowComposition ] = useState(false);
    const [ show3DContent, setShow3DContent ] = useState(false);
    const [ showCompose, setShowCompose ] = useState(false);
    const [ showCounters, setShowCounters ] = useState(false);
    
    const [ onlyMain, setOnlyMain ] = useState(true);

    const [ propertiesCollapsedGroups, setPropertiesCollapsedGroups ] = useState<string[]>([]);
    const [ contentCollapsedGroups, setContentCollapsedGroups ] = useState<string[]>([]);


    return <div className={`${styles.debugValues} ${className ?? ''}`}>
        <h1>Debug Info</h1>
        <div className={styles.toggles}>
            <span className={styles.togglesLabel}>Show:</span>
            <Toggle value={showProperties} onChange={setShowProperties} text="Properties"/>
            <Toggle value={showWorkState} onChange={setShowWorkState} text="Work State"/>
            <Toggle value={showComposition} onChange={setShowComposition} text="Content (JSON)"/>
            <Toggle value={showCompose} onChange={setShowCompose} text="Content (UI)"/>
            <Toggle value={showCounters} onChange={setShowCounters} text="Counters"/>
            <Toggle value={show3DContent} onChange={setShow3DContent} text="3D Content"/>
        </div>
        <div>
            use <code>debugPanel()</code> in the console to toggle this panel,{' '}
            use <code>showInspector()</code> to show the Babylon Inspector.{' '}.
        </div>
        {
            showCompose || showCounters ?
            <div>
                <Toggle value={onlyMain} onChange={setOnlyMain} text="Show only elements in main scene" />
            </div>
            :
            null
        }        
        { (showProperties && !optionsSuccess) ? <li>Loading...</li> : null }
        { showProperties && optionsSuccess ? <ul>
                { composition?.groups.map(group => {
                    const groupImplicitValues = implicitValues[group.id];
                    const isCollapsed = propertiesCollapsedGroups.includes(group.id);
                    return <>
                        <KeyValueSection title={`Group ${group.id} @ ${group.scene}${selectedGroup?.id === group.id ? ' [selected]' : ''}: `}
                            isCollapsed={ isCollapsed }
                            onClick={ isCollapsed ? () => setPropertiesCollapsedGroups( propertiesCollapsedGroups.filter(g => g !== group.id)) 
                                                  : () => setPropertiesCollapsedGroups([...propertiesCollapsedGroups, group.id]) }
                            />
                        { groupImplicitValues && !isCollapsed ?
                          options.properties.map(property => {
                            
                            let value = groupImplicitValues ? groupImplicitValues[property.name] : '---';
                            let jc = justChanged.some(jc => jc[0] === group.id &&  jc[1] === property.name);
                            let implicit = value != null && group.options[property.name] == null;
                            return <KeyValue keyName={property.name} value={value} justChanged={jc} implicit={implicit}/>
                          })
                          :
                          null
                        }
                    </>
                })}
            </ul>
            :
            null
        }
        { showProperties && showWorkState ? <hr/> : null }
        {
            showWorkState ? <ul>
                <KeyValue keyName="mode" value={mode} />
                <KeyValue keyName="currentQuestion" value={currentQuestion ?? <Undefined/>} />
                <KeyValue keyName="currentNSteps" value={currentNSteps ?? <Undefined/>} />
                <KeyValue keyName="currentStep" value={currentStep} />
                <KeyValue keyName="canGoPrevious" value={check(canGoPrevious)} />
                <KeyValue keyName="canGoNext" value={check(canGoNext)} />
                <KeyValue keyName="questionData" value={<pre>{JSON.stringify(questionData,null,2)}</pre>} />
            </ul>
            :
            null
        }
        { (showProperties || showWorkState) && showComposition ? <hr/> : null }
        {
            showComposition ? 
                <>
                    <span className={styles.dvKey}>composition:</span>
                    <span className={styles.dvValue}><pre>{JSON.stringify(composition,null,2)}</pre></span>
                </>
                :
                null
        }
        { (showProperties || showWorkState || showComposition) && showCompose ? <hr/> : null }
        {
            showCompose ?
            <><p>Groups ({composition.groups.length}):</p>
            {
                composition.groups.filter(g => !onlyMain || g.scene === MAIN_SCENE).map(group => {

                    const attachPoints = resolveAttachControlsInfo([group], elements);

                    const delGroup = () => {
                        dispatch(configuratorActions.deleteGroup(group.id));
                    }
                    const rotateGroup = () => {
                        dispatch(configuratorActions.rotateGroup(group.id, group.elements[0].id));
                    }



                    return <div>
                        <div>
                            <strong>Group {group.id} @{ group.scene}</strong>
                            {
                                contentCollapsedGroups.includes(group.id) ?
                                    <button type="button" onClick={e => setContentCollapsedGroups(contentCollapsedGroups.filter(g => g !== group.id))}>[ + ]</button>
                                    :
                                    <button type="button" onClick={e => setContentCollapsedGroups([...contentCollapsedGroups, group.id])}>[ - ]</button>
                            }
                        </div>   
                        {
                            !contentCollapsedGroups.includes(group.id) ?
                            <>
                                <div className={styles.groupControls}>
                                    <button type="button" onClick={rotateGroup}>Rotate</button>
                                    <button type="button" onClick={delGroup}>Delete</button>
                                </div>
                                <div className={styles.groupContent}>
                                {
                                    group.elements.map(element => {

                                        return <div key={element.id}>
                                            <div><strong>Element: {element.id} - {element.element }</strong> {
                                                selectedElement === element.id ?
                                                    <span> (selected)</span>
                                                    :
                                                    null
                                            }</div>
                                            {
                                                attachPoints.filter(ap => ap.elementId === element.id).map(ap => {

                                                    const addAttach = () => {
                                                        dispatch(configuratorActions.startAddAttach({
                                                            elementId: element.id,
                                                            position: ap.position,
                                                            profile: ap.profile
                                                        }));
                                                    };

                                                    return <div className={styles.attachPoint}>
                                                        { ap.position } ({ ap.profile })
                                                        {
                                                            ap.type === 'used' ?
                                                                <span> { ap.attachedToId ? 
                                                                    <>[ attached to {ap.attachedToId}/{ap.attachedToPosition} ]</>
                                                                    :
                                                                    <>[ used ]</>
                                                                } </span>
                                                                :
                                                                <>
                                                                    { ap.type === 'free-required' ? <span> [required] </span> : null}
                                                                    &nbsp;<button type="button" onClick={addAttach}>Add</button>
                                                                </>
                                                        }
                                                    </div>
                                                })
                                            }
                                        </div>
                                    })
                                }
                                </div>
                            </>
                            :
                            null
                        }                     

                        <hr></hr>
                    </div>
                })
            }
            </>
            :
            null
        }
        { (showProperties || showWorkState || showComposition || showCompose ) && showCounters ? <hr/> : null }
        {
            showCounters ? <>
                             <ul>
                             {
                                composition.groups.filter(g => !onlyMain || g.scene === MAIN_SCENE).map(group => {
                                    return <>
                                        <KeyValueSection title={`Group: ${ group.id }`} />
                                        {
                                            group.counters ? (
                                                    (Object.keys(group.counters).length > 0) ? (
                                                        Object.entries(group.counters).map(([k,v]) => <KeyValue keyName={k} value={v}/>)
                                                    )
                                                    :
                                                    <li>Zero counters</li>
                                                )
                                                :
                                                <li>No counters</li>
                                        }
                                    </>
                                }
                                )
                             }
                             </ul>
                           </>
                         :
                         null
        }
        { (showProperties || showWorkState || showComposition || showCompose || showCounters ) && show3DContent ? <hr/> : null }
        {
            show3DContent ?
                <>
                    <span className={styles.dvKey}>3D content:</span>
                    <span className={styles.dvValue}><pre>{JSON.stringify(viewerContent,null,2)}</pre></span>
                </>
                :
                null
        }        
    </div>

}

const Toggle = ({value,text,onChange} : {
    value: boolean
    text: string
    onChange: (v: boolean) => void
}) => {
    return <button type="button" onClick={e => onChange(!value)}>
        { check(value) + ' ' }
        { text }
        
    </button>
}

const KeyValueSection = ({title,isCollapsed,onClick} : {
    title: string
    isCollapsed?: boolean
    onClick?: () => void
}) => {
    return <li>
        <h2 className={styles.dvKVTitle}>
            {title}
            { isCollapsed !== undefined ? <button type="button" onClick={onClick}>[ { isCollapsed ? '+' : '-'} ]</button> : null }
        </h2>
    </li>
}

const KeyValue = ({keyName,value,justChanged,implicit} : {
    keyName: string
    value?: ReactNode
    justChanged?: boolean
    implicit?: boolean
}) => {

    let valueNode;
    let keyClasses = [ styles.dvKey ];
    let valuesClasses = [ styles.dvValue ];

    if(value === undefined) {
        valueNode = <Undefined/>
    } else {
        valueNode = value;
    }

    if(justChanged) {
        keyClasses.push(styles.dvJustChanged);
        valuesClasses.push(styles.dvJustChanged);
    }

    if(implicit) {
        valuesClasses.push(styles.dvImplicit);
    }

    return <li>
        <span className={keyClasses.join(' ')}>{keyName}:</span>
        <span className={valuesClasses.join(' ')}>{valueNode}</span>
    </li>
}

const Undefined = () => {
    return <span className={styles.undefined}>undefined</span>
}

function check(v: boolean) {
    return v ? '✅' : '🔲';
}